import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

export default function PlumbingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [isCallbackSubmitting, setIsCallbackSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: 'residential',
    teamSize: '1-2'
  });
  const [callbackData, setCallbackData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredTime: 'anytime',
    message: ''
  });
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';

  useSEO({
    title: 'Plumbing Quote Software Australia | MtaPlannerPro for Plumbers',
    description: 'Professional quote and invoice software built specifically for Australian plumbers. Generate accurate plumbing quotes for bathroom renovations, hot water systems, pipe fitting, and drainage work with real Bunnings pricing. Save 15+ hours per week on admin.',
    keywords: 'plumbing quote software, plumber invoice app, bathroom renovation quotes, hot water system pricing, plumbing business software, tradie quotes Australia, plumbing job management',
    canonical: `${siteUrl}/industries/plumbing`,
    ogTitle: 'Plumbing Quote Software Australia | MtaPlannerPro',
    ogDescription: 'Generate professional plumbing quotes in minutes with AI-powered pricing, Bunnings material costs, and industry-specific templates.',
    ogUrl: `${siteUrl}/industries/plumbing`,
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Show lead form after 8 seconds
    const timer = setTimeout(() => {
      if (!formSubmitted) {
        setShowLeadForm(true);
      }
    }, 8000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [formSubmitted]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('businessType', formData.businessType);
      formBody.append('teamSize', formData.teamSize);
      
      await fetch('https://readdy.ai/api/form/d69aqktt1k61917cm5d0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });
      
      setFormSubmitted(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCallbackSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', callbackData.name);
      formBody.append('phone', callbackData.phone);
      formBody.append('email', callbackData.email);
      formBody.append('preferredTime', callbackData.preferredTime);
      formBody.append('message', callbackData.message);
      
      await fetch('https://readdy.ai/api/form/d69b7o7mvg9ih2c79udg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });
      
      setCallbackSubmitted(true);
      setShowCallbackForm(false);
      setTimeout(() => setCallbackSubmitted(false), 5000);
    } catch (error) {
      console.error('Callback form submission error:', error);
    } finally {
      setIsCallbackSubmitting(false);
    }
  };

  const plumbingFeatures = [
    {
      icon: 'ri-drop-line',
      title: 'Bathroom Renovation Quotes',
      description: 'Generate detailed quotes for full bathroom renovations including fixtures, tiling, waterproofing, and labor. Upload photos and get instant material lists with Bunnings pricing.',
      stats: '2-3 min per quote'
    },
    {
      icon: 'ri-fire-line',
      title: 'Hot Water System Pricing',
      description: 'Accurate pricing for hot water installations and replacements. Includes gas, electric, and solar options with compliance requirements and warranty details.',
      stats: '95% quote accuracy'
    },
    {
      icon: 'ri-tools-line',
      title: 'Pipe Fitting & Repairs',
      description: 'Quick quotes for pipe repairs, replacements, and new installations. Covers copper, PVC, and PEX piping with fittings, labor rates, and call-out fees.',
      stats: 'All pipe types covered'
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Drainage Work Estimates',
      description: 'Comprehensive drainage quotes including blocked drains, stormwater, and sewer line work. Factor in excavation, materials, and council compliance costs.',
      stats: 'Compliance included'
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Tap & Fixture Installation',
      description: 'Professional quotes for tap replacements, mixer installations, toilet repairs, and fixture upgrades. Includes supply and installation pricing.',
      stats: 'Instant pricing'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Leak Detection & Repairs',
      description: 'Detailed quotes for leak detection services, pipe repairs, and water damage prevention. Includes diagnostic fees and repair work breakdown.',
      stats: 'Emergency rates ready'
    }
  ];

  const plumbingStats = [
    { number: '15+', label: 'Hours Saved Weekly', icon: 'ri-time-line' },
    { number: '3,200+', label: 'Plumbers Using It', icon: 'ri-team-line' },
    { number: '89%', label: 'Quote Win Rate', icon: 'ri-trophy-line' },
    { number: '$12K', label: 'Avg Monthly Revenue Increase', icon: 'ri-money-dollar-circle-line' }
  ];

  const testimonials = [
    {
      name: 'Dave Mitchell',
      business: 'Mitchell Plumbing Services',
      location: 'Melbourne, VIC',
      image: 'https://public.readdy.ai/ai/img_res/bf63d7b6c73a1d84367156a14b266497.jpg',
      quote: 'Cut my quoting time from 45 minutes to under 3 minutes. The Bunnings pricing integration is spot-on for bathroom renos. My quote acceptance rate jumped from 62% to 89% because clients trust the detailed breakdowns.',
      rating: 5,
      jobType: 'Bathroom Renovations Specialist',
      yearsInBusiness: '12 years'
    },
    {
      name: 'Sarah Chen',
      business: 'Chen Hot Water Solutions',
      location: 'Sydney, NSW',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20asian%20plumber%20in%20work%20uniform%20holding%20tablet%20device%20standing%20next%20to%20hot%20water%20system%20installation%20in%20modern%20australian%20home%20bright%20clean%20environment&width=400&height=400&seq=plumber-sarah-chen&orientation=squarish',
      quote: 'Specializing in hot water systems means I need accurate pricing fast. This software knows every brand and model. I can quote Rheem, Rinnai, Dux - everything - with correct installation costs. Game changer for my business.',
      rating: 5,
      jobType: 'Hot Water Systems Expert',
      yearsInBusiness: '8 years'
    },
    {
      name: 'Tony Russo',
      business: 'Russo Emergency Plumbing',
      location: 'Brisbane, QLD',
      image: 'https://readdy.ai/api/search-image?query=experienced%20male%20plumber%20in%20red%20work%20shirt%20with%20tool%20belt%20standing%20beside%20service%20van%20holding%20wrench%20professional%20confident%20pose%20suburban%20australian%20street%20background&width=400&height=400&seq=plumber-tony-russo&orientation=squarish',
      quote: 'Emergency call-outs need fast quotes. I can now send professional quotes from my phone while still on-site. Clients love the transparency and I close more jobs on the spot. Increased my emergency work revenue by 40%.',
      rating: 5,
      jobType: 'Emergency & Repairs',
      yearsInBusiness: '15 years'
    }
  ];

  const pricingHighlights = [
    {
      category: 'Bathroom Fixtures',
      items: ['Toilets', 'Vanities', 'Taps & Mixers', 'Showers', 'Baths'],
      icon: 'ri-home-4-line',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      category: 'Hot Water Systems',
      items: ['Gas Systems', 'Electric Systems', 'Solar Systems', 'Heat Pumps', 'Instantaneous'],
      icon: 'ri-fire-line',
      color: 'from-orange-500 to-red-600'
    },
    {
      category: 'Pipes & Fittings',
      items: ['Copper Pipes', 'PVC Pipes', 'PEX Pipes', 'Fittings', 'Valves'],
      icon: 'ri-git-branch-line',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      category: 'Tools & Materials',
      items: ['Sealants', 'Adhesives', 'Insulation', 'Brackets', 'Fasteners'],
      icon: 'ri-tools-line',
      color: 'from-amber-500 to-orange-600'
    }
  ];

  const caseStudies = [
    {
      title: 'Bathroom Renovation Business Growth',
      plumber: 'Dave Mitchell',
      challenge: 'Spending 6-8 hours weekly on quotes, losing jobs to faster competitors',
      solution: 'Implemented MtaPlannerPro for all bathroom renovation quotes',
      results: [
        'Quote time reduced from 45 min to 3 min',
        'Win rate increased from 62% to 89%',
        'Takes on 12 more jobs per month',
        'Revenue up $18K monthly'
      ],
      image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20bathroom%20renovation%20with%20white%20subway%20tiles%20chrome%20fixtures%20rainfall%20shower%20glass%20screen%20marble%20countertop%20bright%20natural%20lighting%20professional%20photography%20clean%20minimal%20design&width=800&height=500&seq=bathroom-reno-case-study&orientation=landscape'
    },
    {
      title: 'Emergency Plumbing Response Time',
      plumber: 'Tony Russo',
      challenge: 'Losing emergency jobs due to slow quote turnaround',
      solution: 'Mobile quoting with instant pricing for emergency call-outs',
      results: [
        'On-site quotes in under 2 minutes',
        'Emergency job conversion up 65%',
        'Customer satisfaction score: 4.9/5',
        'Emergency revenue up 40%'
      ],
      image: 'https://readdy.ai/api/search-image?query=emergency%20plumbing%20repair%20work%20under%20sink%20with%20professional%20tools%20and%20equipment%20bright%20work%20light%20focused%20hands%20fixing%20pipe%20leak%20clean%20organized%20workspace%20professional%20trade%20photography&width=800&height=500&seq=emergency-plumbing-case&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Floating Callback Button */}
      <button
        onClick={() => setShowCallbackForm(true)}
        className="fixed bottom-6 left-6 z-40 px-6 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-full font-semibold shadow-2xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 flex items-center gap-2"
      >
        <i className="ri-phone-line text-xl"></i>
        <span className="hidden sm:inline">Request Callback</span>
        <span className="sm:hidden">Call Me</span>
      </button>

      {/* Callback Request Form Modal */}
      {showCallbackForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative bg-slate-900 border border-white/10 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowCallbackForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-white"></i>
            </button>
            
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-8 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                <i className="ri-phone-line"></i>
                We'll Call You Back
              </div>
              <h3 className="text-2xl font-bold mb-2">Request a Callback</h3>
              <p className="text-emerald-100">Our team will contact you within 24 hours</p>
            </div>
            
            <form 
              id="callback-request-form"
              data-readdy-form
              onSubmit={handleCallbackSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={callbackData.name}
                  onChange={(e) => setCallbackData({...callbackData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={callbackData.phone}
                  onChange={(e) => setCallbackData({...callbackData, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="0412 345 678"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email (Optional)</label>
                <input
                  type="email"
                  name="email"
                  value={callbackData.email}
                  onChange={(e) => setCallbackData({...callbackData, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="john@plumbingcompany.com.au"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Preferred Call Time</label>
                <select
                  name="preferredTime"
                  value={callbackData.preferredTime}
                  onChange={(e) => setCallbackData({...callbackData, preferredTime: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                >
                  <option value="anytime">Anytime</option>
                  <option value="morning">Morning (8am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 8pm)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Message (Optional)</label>
                <textarea
                  name="message"
                  value={callbackData.message}
                  onChange={(e) => setCallbackData({...callbackData, message: e.target.value})}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm text-white placeholder-slate-500 resize-none"
                  placeholder="Tell us what you'd like to discuss..."
                ></textarea>
                <div className="text-xs text-slate-500 mt-1 text-right">{callbackData.message.length}/500</div>
              </div>
              
              <button
                type="submit"
                disabled={isCallbackSubmitting}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isCallbackSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-phone-line"></i>
                    Request Callback
                  </span>
                )}
              </button>
              
              <p className="text-xs text-slate-500 text-center">
                <i className="ri-shield-check-line mr-1"></i>
                We'll contact you within 24 hours during business days
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Callback Success Toast */}
      {callbackSubmitted && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Callback Request Received!</div>
            <div className="text-emerald-100 text-sm">We'll contact you within 24 hours</div>
          </div>
        </div>
      )}

      {/* Floating Lead Capture Form */}
      {showLeadForm && !formSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative bg-slate-900 border border-white/10 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-white"></i>
            </button>
            
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 p-8 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                <i className="ri-drop-line"></i>
                Plumbers Only
              </div>
              <h3 className="text-2xl font-bold mb-2">Get Your Free Plumbing Business Audit</h3>
              <p className="text-blue-100">Plus 30-day extended trial when you book a demo today</p>
            </div>
            
            <form 
              id="plumbing-lead-form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Your Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="john@plumbingcompany.com.au"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="0412 345 678"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Specialization</label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={(e) => setFormData({...formData, businessType: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="bathroom-reno">Bathroom Renos</option>
                    <option value="hot-water">Hot Water</option>
                    <option value="drainage">Drainage</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Team Size</label>
                  <select
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={(e) => setFormData({...formData, teamSize: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                  >
                    <option value="1-2">Just me / 1-2</option>
                    <option value="3-5">3-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="10+">10+ people</option>
                  </select>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    Get My Free Audit + Demo
                  </span>
                )}
              </button>
              
              <p className="text-xs text-slate-500 text-center">
                <i className="ri-shield-check-line mr-1"></i>
                Your information is secure and will never be shared
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Sticky Bottom CTA Bar */}
      {showStickyBar && !formSubmitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-blue-600 to-cyan-600 shadow-2xl shadow-black/20 transform transition-transform duration-300">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full">
                <i className="ri-drop-fill text-white"></i>
                <span className="text-white text-sm font-medium">Plumbers Only</span>
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">Get 30% Off Your First 3 Months</div>
                <div className="text-blue-100 text-sm hidden sm:block">Plus free business audit worth $499</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLeadForm(true)}
                className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line mr-2"></i>
                Claim Offer
              </button>
              <button
                onClick={() => setShowStickyBar(false)}
                className="p-2 text-white/80 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {formSubmitted && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Thanks! We'll be in touch soon</div>
            <div className="text-emerald-100 text-sm">Check your email for confirmation</div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-slate-950/95 backdrop-blur-xl shadow-lg shadow-black/10 border-b border-white/5'
            : 'bg-slate-950/80 backdrop-blur-lg border-b border-white/5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=elite%20modern%20minimalist%20logo%20design%20with%20golden%20metallic%20shield%20emblem%20featuring%20stylized%20letter%20M%20and%20construction%20tools%20crossed%20hammer%20and%20wrench%20on%20premium%20black%20background%20luxury%20professional%20business%20branding%20with%20sharp%20geometric%20lines%20and%20sophisticated%20typography%20high%20end%20corporate%20identity%203d%20rendered%20with%20subtle%20gold%20gradients%20and%20reflective%20surfaces&width=200&height=200&seq=mtaplanner-elite-logo-001&orientation=squarish"
                  alt="MtaPlannerPro Elite Logo"
                  className="w-11 h-11 object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-950 animate-pulse"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-lg leading-tight tracking-tight">
                  MtaPlannerPro
                </span>
                <span className="text-amber-400 text-[10px] font-semibold uppercase tracking-wider">
                  For Plumbers
                </span>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/"
                className="relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap text-slate-300 hover:text-white hover:bg-white/5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-home-5-line text-base"></i>
                  Home
                </span>
              </Link>
              <Link
                to="/invoice"
                className="relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap text-slate-300 hover:text-white hover:bg-white/5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-file-text-line text-base"></i>
                  Quote Generator
                </span>
              </Link>
            </div>

            <Link
              to="/demo"
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
            >
              <i className="ri-play-circle-line mr-2"></i>
              Book a Demo
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-blue-400 rounded-full text-sm font-semibold mb-6">
                <i className="ri-drop-line"></i>
                Built for Australian Plumbers
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Professional Plumbing Quotes in
                <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Under 3 Minutes
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Generate accurate quotes for bathroom renovations, hot water systems, pipe fitting, and drainage work. AI-powered pricing with real Bunnings material costs. Trusted by 3,200+ Australian plumbers.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <Link
                  to="/demo"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
                >
                  <i className="ri-play-circle-line mr-2"></i>
                  See It In Action
                </Link>
                <Link
                  to="/invoice"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-white/10 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Try Quote Generator
                </Link>
              </div>

              <div className="flex items-center gap-6 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-400 text-lg"></i>
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-400 text-lg"></i>
                  <span>14-day free trial</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://readdy.ai/api/search-image?query=premium%20professional%20australian%20plumber%20in%20pristine%20uniform%20working%20on%20luxury%20modern%20bathroom%20renovation%20installing%20gleaming%20chrome%20tap%20fixture%20with%20advanced%20professional%20tools%20pristine%20white%20marble%20tiles%20sophisticated%20interior%20design%20bright%20natural%20lighting%20ultra%20sharp%20focus%20high%20end%20construction%20photography%20cinematic%20quality&width=1200&height=800&seq=plumbing-hero-main-premium&orientation=landscape"
                alt="Professional plumber working on bathroom renovation"
                className="relative rounded-3xl shadow-2xl shadow-blue-500/20 w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">2.8 min</div>
                    <div className="text-sm text-slate-400">Avg Quote Time</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">vs 45 min traditional method</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {plumbingStats.map((stat, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-blue-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${stat.icon} text-blue-400 text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-blue-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-tools-line"></i>
              Plumbing-Specific Features
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Everything You Need for
              <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Plumbing Quotes
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Purpose-built features for bathroom renovations, hot water systems, pipe work, and drainage jobs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plumbingFeatures.map((feature, index) => (
              <div
                key={index}
                className="group bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-blue-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${feature.icon} text-blue-400 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 mb-4 leading-relaxed">{feature.description}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-sm text-blue-400 font-medium">
                  <i className="ri-flashlight-line text-xs"></i>
                  {feature.stats}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-blue-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-play-circle-line"></i>
              See It In Action
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Watch How Easy
              <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Plumbing Quotes Can Be
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              See how plumbers create professional quotes in under 3 minutes
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Main Demo Video */}
            <div className="relative group cursor-pointer" onClick={() => window.open('/demo', '_self')}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20plumber%20using%20tablet%20software%20to%20create%20bathroom%20renovation%20quote%20in%20modern%20bathroom%20setting%20with%20fixtures%20and%20tools%20visible%20clean%20bright%20environment%20showing%20software%20interface%20on%20screen&width=800&height=500&seq=plumb-demo-video-1&orientation=landscape"
                  alt="Plumbing Quoting Demo"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-play-fill text-4xl text-white ml-1"></i>
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-white font-bold text-xl mb-2">Complete Bathroom Reno Quote in 2:52</div>
                  <div className="text-white/80 text-sm">Watch Dave create a $12,500 bathroom renovation quote</div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                <i className="ri-time-line mr-1"></i>
                2:52 min
              </div>
            </div>

            {/* Demo Features List */}
            <div className="space-y-6">
              <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-blue-500/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-camera-line text-blue-400 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">Photo-Based Quoting</h4>
                    <p className="text-slate-400 text-sm">Upload photos of the bathroom and our AI identifies fixtures and suggests pricing automatically.</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-cyan-500/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-price-tag-3-line text-cyan-400 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">Bunnings Pricing Integration</h4>
                    <p className="text-slate-400 text-sm">Material costs update automatically from Bunnings Warehouse with real-time pricing.</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-pdf-line text-emerald-400 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">Professional PDFs</h4>
                    <p className="text-slate-400 text-sm">Generate branded PDF quotes with your logo, terms, and itemized breakdowns in one click.</p>
                  </div>
                </div>
              </div>

              <Link 
                to="/demo"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-xl"></i>
                Watch Full Demo
              </Link>
            </div>
          </div>

          {/* Quick Demo Videos Grid */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 cursor-pointer group">
              <div className="relative h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=hot%20water%20system%20installation%20quote%20being%20created%20on%20tablet%20screen%20with%20hot%20water%20heater%20visible%20in%20background%20professional%20plumber%20reviewing%20estimate%20clean%20modern%20interface&width=600&height=400&seq=plumb-demo-hotwater-1&orientation=landscape"
                  alt="Hot Water Quote Demo"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-xl">
                    <i className="ri-play-fill text-2xl text-white ml-0.5"></i>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-white font-semibold">Hot Water System Quote</div>
                  <div className="text-white/70 text-sm">1:45 min</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 cursor-pointer group">
              <div className="relative h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=drainage%20repair%20quote%20on%20laptop%20screen%20with%20plumber%20inspecting%20drain%20pipe%20in%20background%20professional%20trade%20software%20interface%20clean%20modern%20design&width=600&height=400&seq=plumb-demo-drainage-1&orientation=landscape"
                  alt="Drainage Quote Demo"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-xl">
                    <i className="ri-play-fill text-2xl text-white ml-0.5"></i>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-white font-semibold">Drainage Work Quote</div>
                  <div className="text-white/70 text-sm">2:08 min</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 cursor-pointer group">
              <div className="relative h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=emergency%20plumbing%20repair%20quote%20on%20mobile%20phone%20screen%20with%20plumber%20at%20job%20site%20fixing%20leak%20professional%20trade%20app%20interface%20clean%20modern%20design&width=600&height=400&seq=plumb-demo-emergency-1&orientation=landscape"
                  alt="Emergency Quote Demo"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-xl">
                    <i className="ri-play-fill text-2xl text-white ml-0.5"></i>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-white font-semibold">Emergency On-Site Quote</div>
                  <div className="text-white/70 text-sm">1:22 min</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Integration Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-emerald-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-price-tag-3-line"></i>
              Real-Time Bunnings Pricing
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Accurate Material Pricing
              <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                From Bunnings Warehouse
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Every plumbing material you need with up-to-date pricing from Australia's trusted supplier
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingHighlights.map((category, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${category.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-4">{category.category}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                      <i className="ri-check-line text-emerald-400 text-xs"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/Bunnings_Warehouse_logo.svg/320px-Bunnings_Warehouse_logo.svg.png"
                alt="Bunnings Warehouse"
                className="h-8 opacity-80"
              />
              <span className="text-slate-400 text-lg">Official Pricing Partner</span>
            </div>
            <p className="text-slate-300 max-w-2xl mx-auto">
              All material prices automatically updated from Bunnings Warehouse. Includes pipes, fittings, fixtures, hot water systems, and all plumbing supplies.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-star-line"></i>
              Trusted by Plumbers
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Real Results from
              <span className="block bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                Real Plumbers
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-blue-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-blue-500/30"
                  />
                  <div>
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-slate-400">{testimonial.business}</div>
                    <div className="text-xs text-slate-500">{testimonial.location}</div>
                  </div>
                </div>

                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400"></i>
                  ))}
                </div>

                <p className="text-slate-300 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="text-sm">
                    <div className="text-blue-400 font-semibold">{testimonial.jobType}</div>
                    <div className="text-slate-500 text-xs">{testimonial.yearsInBusiness}</div>
                  </div>
                  <i className="ri-verified-badge-fill text-emerald-400 text-2xl"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-blue-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-video-line"></i>
              Video Testimonials
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Hear From Real
              <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Plumbing Professionals
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Watch how plumbers transformed their businesses with MtaPlannerPro
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Featured Video Testimonial */}
            <div className="lg:row-span-2 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden">
              <div className="relative h-80 lg:h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20australian%20plumber%20in%20workshop%20giving%20video%20testimonial%20with%20plumbing%20equipment%20and%20tools%20in%20background%20warm%20lighting%20professional%20setting%20clean%20modern%20environment&width=800&height=600&seq=plumb-video-test-featured&orientation=landscape"
                  alt="Featured Video Testimonial"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent lg:hidden"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-2xl cursor-pointer hover:scale-110 transition-transform duration-300">
                    <i className="ri-play-fill text-5xl text-white ml-1"></i>
                  </div>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-full text-sm font-semibold">
                  <i className="ri-star-fill mr-1"></i>
                  Featured Story
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20male%20plumber%20headshot%20portrait%20confident%20smile%20wearing%20work%20shirt%20clean%20background%20natural%20lighting%20australian%20tradesman&width=200&height=200&seq=plumb-video-avatar-1&orientation=squarish"
                    alt="Dave Mitchell"
                    className="w-14 h-14 rounded-full object-cover border-2 border-blue-500"
                  />
                  <div>
                    <div className="text-white font-bold text-lg">Dave Mitchell</div>
                    <div className="text-slate-400">Mitchell Plumbing, Melbourne</div>
                  </div>
                </div>
                <p className="text-slate-300 leading-relaxed mb-4">
                  "I went from spending 8 hours a week on quotes to just 1 hour. The Bunnings pricing integration is spot-on. Now I'm taking on 12 more jobs per month."
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <i className="ri-arrow-up-line"></i>
                    <span className="font-semibold">$18K/month increase</span>
                  </div>
                  <div className="text-slate-500">|</div>
                  <div className="text-slate-400 text-sm">
                    <i className="ri-time-line mr-1"></i>
                    4:15 min
                  </div>
                </div>
              </div>
            </div>

            {/* Smaller Video Testimonials */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 cursor-pointer group">
              <div className="relative h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=female%20plumber%20giving%20video%20testimonial%20in%20modern%20bathroom%20setting%20with%20plumbing%20fixtures%20visible%20professional%20environment%20warm%20lighting&width=600&height=400&seq=plumb-video-test-2&orientation=landscape"
                  alt="Video Testimonial"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-play-fill text-3xl text-white ml-0.5"></i>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20female%20plumber%20headshot%20portrait%20confident%20smile%20clean%20background%20natural%20lighting%20australian%20tradeswoman&width=200&height=200&seq=plumb-video-avatar-2&orientation=squarish"
                    alt="Sarah Chen"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-white">Sarah Chen</div>
                    <div className="text-sm text-slate-400">Chen Hot Water Solutions, Sydney</div>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">"The hot water system quoting is incredible. Every brand, every model, accurate pricing."</p>
                <div className="flex items-center justify-between">
                  <div className="text-emerald-400 text-sm font-semibold">
                    <i className="ri-arrow-up-line mr-1"></i>
                    23 hot water jobs/month
                  </div>
                  <div className="text-slate-500 text-sm">2:32 min</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 cursor-pointer group">
              <div className="relative h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=experienced%20plumber%20giving%20video%20testimonial%20next%20to%20service%20van%20with%20tools%20visible%20professional%20environment%20natural%20lighting%20suburban%20setting&width=600&height=400&seq=plumb-video-test-3&orientation=landscape"
                  alt="Video Testimonial"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-play-fill text-3xl text-white ml-0.5"></i>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20male%20plumber%20headshot%20portrait%20experienced%20confident%20clean%20background%20natural%20lighting%20australian%20tradesman%20mature&width=200&height=200&seq=plumb-video-avatar-3&orientation=squarish"
                    alt="Tony Russo"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-white">Tony Russo</div>
                    <div className="text-sm text-slate-400">Russo Emergency Plumbing, Brisbane</div>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">"On-site quotes from my phone. Clients love the transparency. Emergency revenue up 40%."</p>
                <div className="flex items-center justify-between">
                  <div className="text-emerald-400 text-sm font-semibold">
                    <i className="ri-arrow-up-line mr-1"></i>
                    40% more emergency jobs
                  </div>
                  <div className="text-slate-500 text-sm">3:18 min</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link 
              to="/demo"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-video-line text-xl"></i>
              Watch More Success Stories
            </Link>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-trophy-line"></i>
              Success Stories
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              How Plumbers Are Growing
              <span className="block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Their Businesses
              </span>
            </h2>
          </div>

          <div className="space-y-12">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="relative h-80 lg:h-auto">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent lg:hidden"></div>
                  </div>
                  <div className="p-8 lg:p-12">
                    <h3 className="text-2xl font-bold text-white mb-6">{study.title}</h3>
                    <div className="flex items-center gap-2 text-blue-400 font-semibold mb-6">
                      <i className="ri-user-line"></i>
                      {study.plumber}
                    </div>

                    <div className="space-y-4 mb-6">
                      <div>
                        <div className="text-sm font-semibold text-slate-400 mb-2">Challenge</div>
                        <p className="text-slate-300">{study.challenge}</p>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-400 mb-2">Solution</div>
                        <p className="text-slate-300">{study.solution}</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-6">
                      <div className="text-sm font-semibold text-emerald-400 mb-3">Results</div>
                      <ul className="space-y-2">
                        {study.results.map((result, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-slate-300">
                            <i className="ri-check-double-line text-emerald-400 text-lg mt-0.5"></i>
                            <span>{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inline Lead Capture Section before CTA Section */}
      {/* Inline Lead Capture Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-blue-400 rounded-full text-sm font-semibold mb-6">
                <i className="ri-customer-service-2-line"></i>
                Talk to a Plumbing Expert
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Get a Personalized Demo for
                <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Your Plumbing Business
                </span>
              </h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                See exactly how MtaPlannerPro can help you quote bathroom renos, hot water systems, and drainage work faster than ever.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <i className="ri-time-line text-blue-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">30-Minute Personalized Demo</div>
                    <div className="text-slate-400 text-sm">Tailored to your plumbing specialty</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <i className="ri-gift-line text-emerald-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Free Business Audit ($499 Value)</div>
                    <div className="text-slate-400 text-sm">Identify opportunities to grow</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <i className="ri-percent-line text-purple-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">30% Off First 3 Months</div>
                    <div className="text-slate-400 text-sm">Exclusive offer for demo attendees</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Book Your Free Demo</h3>
                <p className="text-slate-400">Fill out the form and we'll contact you within 24 hours</p>
              </div>
              
              <form 
                id="plumbing-inline-lead-form"
                data-readdy-form
                onSubmit={handleFormSubmit}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Work Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                    placeholder="you@company.com.au"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                    placeholder="0412 345 678"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Specialization</label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={(e) => setFormData({...formData, businessType: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="bathroom-reno">Bathroom Renos</option>
                      <option value="hot-water">Hot Water</option>
                      <option value="drainage">Drainage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Team Size</label>
                    <select
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={(e) => setFormData({...formData, teamSize: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                    >
                      <option value="1-2">1-2 people</option>
                      <option value="3-5">3-5 people</option>
                      <option value="6-10">6-10 people</option>
                      <option value="10+">10+ people</option>
                    </select>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-calendar-check-line"></i>
                      Book My Free Demo
                    </span>
                  )}
                </button>
                
                <div className="flex items-center justify-center gap-4 pt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-shield-check-line"></i>
                    Secure
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-spam-line"></i>
                    No spam
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    Response in 24h
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-3xl p-12 text-center overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_70%)]"></div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded-full text-sm font-semibold mb-6">
                <i className="ri-drop-line"></i>
                Join 3,200+ Australian Plumbers
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Start Creating Professional
                <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Plumbing Quotes Today
                </span>
              </h2>
              
              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                14-day free trial. No credit card required. Cancel anytime. Start winning more plumbing jobs with professional quotes.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/demo"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
                >
                  <i className="ri-play-circle-line mr-2"></i>
                  Book Your Demo
                </Link>
                <Link
                  to="/invoice"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-white/10 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Try Quote Generator
                </Link>
              </div>

              <div className="flex items-center justify-center gap-8 mt-10 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-emerald-400 text-lg"></i>
                  <span>Secure & Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-customer-service-2-line text-blue-400 text-lg"></i>
                  <span>Australian Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-smartphone-line text-purple-400 text-lg"></i>
                  <span>Mobile Ready</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-slate-950/50 backdrop-blur-xl py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=elite%20modern%20minimalist%20logo%20design%20with%20golden%20metallic%20shield%20emblem%20featuring%20stylized%20letter%20M%20and%20construction%20tools%20crossed%20hammer%20and%20wrench%20on%20premium%20black%20background%20luxury%20professional%20business%20branding%20with%20sharp%20geometric%20lines%20and%20sophisticated%20typography%20high%20end%20corporate%20identity%203d%20rendered%20with%20subtle%20gold%20gradients%20and%20reflective%20surfaces&width=200&height=200&seq=mtaplanner-elite-logo-001&orientation=squarish"
                  alt="MtaPlannerPro Elite Logo"
                  className="w-10 h-10 object-contain"
                />
                <span className="text-white font-bold text-lg">MtaPlannerPro</span>
              </div>
              <p className="text-slate-400 text-sm">
                Professional quote software built for Australian tradies.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Industries</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/industries/plumbing" className="text-blue-400 hover:text-blue-300 transition-colors cursor-pointer">
                    Plumbing
                  </Link>
                </li>
                <li>
                  <Link to="/industries/electrical" className="text-slate-400 hover:text-white transition-colors cursor-pointer">
                    Electrical
                  </Link>
                </li>
                <li>
                  <Link to="/industries/hvac" className="text-slate-400 hover:text-white transition-colors cursor-pointer">
                    HVAC
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/invoice" className="text-slate-400 hover:text-white transition-colors cursor-pointer">
                    Quote Generator
                  </Link>
                </li>
                <li>
                  <Link to="/demo" className="text-slate-400 hover:text-white transition-colors cursor-pointer">
                    Book Demo
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-slate-400 hover:text-white transition-colors cursor-pointer">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:support@mtaplannerpro.com" className="text-slate-400 hover:text-white transition-colors cursor-pointer">
                    Contact Us
                  </a>
                </li>
                <li>
                  <Link to="/resources" className="text-slate-400 hover:text-white transition-colors cursor-pointer">
                    Resources
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2024 MtaPlannerPro. All rights reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-400 text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
